import React, { useState } from 'react';
import { StarReason, User } from '../../../types';
import { StarService } from '../services/StarService';
import '../styles/StarGivingModal.css';

interface StarGivingModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: User;
  teacherId: string;
  squadId: string;
  onStarAwarded?: (success: boolean, message: string) => void;
}

const StarGivingModal: React.FC<StarGivingModalProps> = ({
  isOpen,
  onClose,
  student,
  teacherId,
  squadId,
  onStarAwarded
}) => {
  const [selectedReason, setSelectedReason] = useState<StarReason | null>(null);
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const reasons = StarService.getAvailableReasons();

  const handleReasonSelect = (reason: StarReason) => {
    setSelectedReason(reason);
    if (reason !== 'other') {
      setCustomReason('');
    }
  };

  const handleSubmit = async () => {
    if (!selectedReason) return;

    if (selectedReason === 'other' && !customReason.trim()) {
      void window.appAlert('Please specify a reason when selecting "Other"');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await StarService.giveStarToStudent(
        student.id,
        teacherId,
        squadId,
        selectedReason,
        selectedReason === 'other' ? customReason.trim() : ''
      );

      if (result.success) {
        setShowSuccess(true);
        onStarAwarded?.(true, result.message);

        // Play success animation then close
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        void window.appAlert(result.message);
        onStarAwarded?.(false, result.message);
      }
    } catch (error) {
      console.error('Error awarding star:', error);
      const errorMessage = 'Failed to award star. Please try again.';
      void window.appAlert(errorMessage);
      onStarAwarded?.(false, errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason(null);
    setCustomReason('');
    setIsSubmitting(false);
    setShowSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div className="star-modal-backdrop" onClick={handleClose}>
        <div
          className={`star-modal-content ${showSuccess ? 'success' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {!showSuccess ? (
            <>
              {/* Modal Header */}
              <div className="star-modal-header">
                <h2 className="star-modal-title">
                  Why does <span className="student-name-highlight">{student.displayName}</span> get a star?
                </h2>
                <button
                  className="star-modal-close"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>

              {/* Reason Selection */}
              <div className="reason-selection-grid">
                {reasons.map((reason) => (
                  <button
                    key={reason.value}
                    className={`reason-button ${selectedReason === reason.value ? 'selected' : ''}`}
                    onClick={() => handleReasonSelect(reason.value)}
                    disabled={isSubmitting}
                  >
                    <span className="reason-emoji">{getReasonEmoji(reason.value)}</span>
                    <span className="reason-label">{reason.label}</span>
                  </button>
                ))}
              </div>

              {/* Custom Reason Input */}
              {selectedReason === 'other' && (
                <div className="custom-reason-section">
                  <label htmlFor="custom-reason" className="custom-reason-label">
                    Please specify:
                  </label>
                  <input
                    id="custom-reason"
                    type="text"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Enter custom reason..."
                    className="custom-reason-input"
                    maxLength={100}
                    disabled={isSubmitting}
                    autoFocus
                  />
                  <div className="character-count">
                    {customReason.length}/100
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="star-modal-actions">
                <button
                  className="cancel-button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className={`award-button ${selectedReason ? 'enabled' : ''}`}
                  onClick={handleSubmit}
                  disabled={!selectedReason || isSubmitting || (selectedReason === 'other' && !customReason.trim())}
                >
                  {isSubmitting ? (
                    <>
                      <span className="button-spinner"></span>
                      Awarding...
                    </>
                  ) : (
                    <>
                      <span className="award-star-emoji">⭐</span>
                      Award Star
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            /* Success State */
            <div className="success-content">
              <div className="success-animation">
                <div className="success-star">⭐</div>
                <div className="success-sparkles">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className={`sparkle sparkle-${i}`}>✨</div>
                  ))}
                </div>
              </div>
              <h2 className="success-title">Star Awarded!</h2>
              <p className="success-message">
                <span className="student-name-highlight">{student.displayName}</span> received a star for{' '}
                <span className="reason-highlight">
                  {StarService.getReasonDisplayText(selectedReason!, customReason)}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Helper function to get emoji for each reason
const getReasonEmoji = (reason: StarReason): string => {
  const emojiMap: Record<StarReason, string> = {
    creativity: '🎨',
    teamwork: '🤝',
    leadership: '👑',
    kindness: '💝',
    persistence: '💪',
    focus: '🎯',
    positivity: '😊',
    other: '💭'
  };

  return emojiMap[reason] || '⭐';
};

export default StarGivingModal;